/**
 * respawn.ts
 * Nova respawn utilities for death and level-exit handling.
 *
 * Extracted from physicsStep.ts to keep that module under 300 lines.
 *
 * @module respawn
 */

import { gameState } from './gameState';
import { snapCameraToNova } from './camera';
import { NOVA_WIDTH, NOVA_HEIGHT } from '../../constants/gameConstants';
import Logger from '../utils/logger';

/** Optional callback invoked after deathRespawn resets Nova (but not on game over). */
let _deathRespawnHook: (() => void) | null = null;

/**
 * Register a callback that runs after each death-respawn.
 * PlayScene uses this to reset enemies and collectibles to checkpoint state.
 * Pass null to clear.
 * @param fn - Callback to run after death-respawn, or null.
 */
export function setDeathRespawnHook(fn: (() => void) | null): void {
  _deathRespawnHook = fn;
}

/**
 * Reset Nova to the checkpoint spawn position.
 * Used for pit deaths and level-exit loops.
 * Snaps camera to the correct clamped position centered on Nova.
 *
 * Note: checkpointSpawnX/Y are initialized to the level spawn in initGameState()
 * and kept in sync with the active checkpoint by PlayScene.update().
 */
export function respawnNova(): void {
  const nova = gameState.nova;
  nova.x = gameState.checkpointSpawnX;
  nova.y = gameState.checkpointSpawnY;
  nova.vx = 0;
  nova.vy = 0;
  nova.isGrounded = false;
  nova.coyoteFrames = 0;
  nova.jumpBufferFrames = 0;
  nova.isJumping = false;
  nova.isStomping = false;
  nova.hurtFlash = false;
  nova.state = 'IDLE';
  nova.invincibilityFrames = 0;
  nova.facingRight = true;
  nova.animTransitionFrames = 0;
  nova.prevGrounded = true;
  // DECISION: Clear the air-jump bank on respawn so the player can't carry a
  // banked mid-air jump from a pre-death frame across the reset. Nova
  // respawns airborne; the next landing refills it normally via physicsStep.
  nova.airJumpsRemaining = 0;

  // DECISION: Snap camera to Nova's new position immediately, avoiding a blank/
  // incorrect viewport frame when respawning to mid-level checkpoints.
  const cam = snapCameraToNova(
    nova.x,
    nova.y,
    NOVA_WIDTH,
    NOVA_HEIGHT,
    gameState.level.width,
    gameState.level.height,
  );
  gameState.camera.x = cam.x;
  gameState.camera.y = cam.y;
}

/**
 * Death respawn — decrements a life and either respawns or triggers game over.
 * On successful respawn (lives > 0): refills health to Nova's current
 * maxHealth and repositions.
 * On game over (lives reach 0): sets gameOver flag without resetting health.
 * Called for any death: health reaching 0, pit/death-plane, or other fatal conditions.
 */
export function deathRespawn(): void {
  gameState.lives = Math.max(0, gameState.lives - 1);
  Logger.debug('Respawn', `Lives remaining: ${gameState.lives}`);

  // DECISION: Clear the transient post-combat boss-cleared signal. If Nova
  // dies in the same tick combat splices a boss, the flag must not survive
  // to the next tick — otherwise the post-combat exit gate would flip
  // levelComplete immediately after respawn.
  gameState.bossDefeated = false;

  if (gameState.lives <= 0) {
    // DECISION: Set gameOver flag and stop the game loop. The React layer reads
    // this flag to show the game-over overlay. Don't respawn — the player must
    // choose Restart or Quit.
    gameState.gameOver = true;
    gameState.isRunning = false;
    Logger.info('Respawn', 'Game over — no lives remaining');
    return;
  }

  // DECISION: Refill to the *current* maxHealth, not NOVA_MAX_HP. Heart
  // Fragments raise maxHealth above the base 3, and hardcoding the constant
  // here would respawn an upgraded Nova already missing the extra hearts.
  // Mirrors `restoreCheckpointSnapshot`, which restores to the snapshot's
  // maxHealth for the same reason.
  gameState.nova.health = gameState.nova.maxHealth;
  respawnNova();

  // DECISION: Fire the hook AFTER Nova is repositioned so the PlayScene
  // resetToCheckpoint() sees the correct spawn position.
  if (_deathRespawnHook) _deathRespawnHook();
}
